import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { siteInfo } from "@/mocks/site";
import { getCategoryName } from "@/utils/content";
import type { Article } from "@/types/content";
import ArticleSidebar from "@/pages/article/components/ArticleSidebar";

export default function ArticleView({ article }: { article: Article }) {
  const related = articles
    .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
    .slice(0, 4);
  const recommendations = (
    related.length >= 3 ? related : articles.filter((a) => a.id !== article.id)
  ).slice(0, 3);
  let h2Count = 0;

  useSeo({
    title: `${article.title} - 17爆料网`,
    description: article.summary,
    keywords: article.tags.join(","),
    path: `/article/${article.id}`,
    type: "article",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.summary,
        datePublished: article.date,
        author: { "@type": "Organization", name: siteInfo.name },
        publisher: { "@type": "Organization", name: siteInfo.name },
        articleSection: getCategoryName(article.categoryId),
        inLanguage: "zh-CN",
        keywords: article.tags.join(","),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17baoliao.example.com/" },
          { "@type": "ListItem", position: 2, name: "热点聚合", item: "https://17baoliao.example.com/topics" },
          {
            "@type": "ListItem",
            position: 3,
            name: article.title,
            item: `https://17baoliao.example.com/article/${article.id}`,
          },
        ],
      },
    ],
  });

  return (
    <>
      <div className="w-full px-4 pt-6 md:px-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "热点聚合", to: "/topics" },
            { label: getCategoryName(article.categoryId), to: `/topics?cat=${article.categoryId}` },
            { label: article.title },
          ]}
        />
      </div>

      <article className="w-full px-4 pb-10 pt-6 md:px-8 md:pb-14">
        <header className="border-b border-background-200 pb-8">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/topics?cat=${article.categoryId}`}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              {getCategoryName(article.categoryId)}
            </Link>
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                {tag}
              </span>
            ))}
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span className="text-foreground-800">{article.author}</span>
            <span>整理于 {article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟阅读
            </span>
          </div>
        </header>

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0">
            <figure className="overflow-hidden rounded-lg border border-background-200">
              <div className="aspect-[16/9] w-full bg-background-200">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 热点整理`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="border-t border-background-200 bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
                配图为编辑整理用的示意图，与事件当事人无关。
              </figcaption>
            </figure>

            <div className="mt-8 max-w-3xl">
              {article.content.map((block, index) => {
                if (block.type === "h2") {
                  h2Count += 1;
                  return (
                    <h2
                      key={`${block.type}-${index}`}
                      id={`section-${h2Count}`}
                      className="mt-9 scroll-mt-24 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "h3") {
                  return (
                    <h3 key={`${block.type}-${index}`} className="mt-6 font-heading text-base font-bold text-foreground-900 md:text-lg">
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === "quote") {
                  return (
                    <blockquote
                      key={`${block.type}-${index}`}
                      className="mt-6 border-l-4 border-accent-400 bg-accent-50 px-5 py-4 font-heading text-base leading-relaxed text-accent-950"
                    >
                      {block.text}
                    </blockquote>
                  );
                }
                return (
                  <p key={`${block.type}-${index}`} className="mt-4 text-base leading-loose text-foreground-700">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-git-commit-line text-xl text-primary-500"></i>
                事件时间线
              </h2>
              <p className="mt-2 text-sm text-foreground-600">以下节点均来自公开可查的信息，按发生顺序排列。</p>
              <ol className="mt-6 border-l border-background-300 pl-6">
                {article.timeline.map((item) => (
                  <li key={item.time} className="relative pb-7 last:pb-0">
                    <span className="absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full border-2 border-background-100 bg-secondary-500"></span>
                    <p className="text-xs font-medium text-primary-600">{item.time}</p>
                    <h3 className="mt-1 font-heading text-base font-bold text-foreground-900">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.detail}</p>
                  </li>
                ))}
              </ol>
            </section>

            <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-relaxed text-foreground-600">
                发现信息有误？欢迎提交可核对的材料，我们会在一到两个工作日内复核并更新。
              </p>
              <Link
                to="/contact"
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-edit-2-line text-base"></i>
                提交更正
              </Link>
            </div>
          </div>

          <ArticleSidebar article={article} related={recommendations} />
        </div>
      </article>

      <section className="border-t border-background-200 bg-background-100">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续阅读</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recommendations.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}